import { assessJellyfishRisk } from "./swim-analysis";
import { formatTideTime } from "./tide-format";
import type { LocationMetadata } from "./unified-types";
import {
  WIND_SPORTS_OFFSHORE_USABLE_WIND_KMH,
  classifyOffshoreSector,
  type OffshoreSectorResult,
} from "./offshore-sector";
import {
  STORM_FORECAST_UNAVAILABLE_DETAIL,
  STORM_FORECAST_UNAVAILABLE_KEY,
  STORM_FORECAST_UNAVAILABLE_LABEL,
} from "./safety-aggregation";
import {
  SUP_SURF_OFFSHORE_DRIFT_THRESHOLDS,
  SUP_TOURING_OFFSHORE_DRIFT_THRESHOLDS,
  assessSupOffshoreDrift,
  type SupOffshoreDriftAssessment,
  type SupOffshoreDriftThresholds,
} from "./sup-offshore-drift";

export type CoastalSport =
  | "surf" | "bodyboarding" | "foil" | "downwind-foil" | "wing-foil"
  | "wind" | "sup" | "sup-touring" | "sup-surf" | "sup-downwind"
  | "swim" | "diving";

export type CoastalBriefingInput = {
  sport: CoastalSport;
  waterTempC: number | null;
  sstC: number | null;
  month: number; // 1-12
  swellHeightM: number;
  windSpeedKmh: number;
  windDirDeg: number | null;
  beachFacingDeg: number | null;
  /** Local decimal hours of the day's low waters (for example 13.47 for 13:28). */
  lowTideHours: number[];
  /**
   * False when the low-water times are the location-blind lunar estimate
   * (tide datum "none"): the tide hazards still fire but name no time.
   * Defaults to true.
   */
  lowTideTimeKnown?: boolean;
  bottomType: string | null;
  seabed: "sand" | "rock" | "coral" | "mixed" | null;
  nearbyCsoCount: number;
  trailingRain24h: number | null;
  maxWaveSize: string | null;
  difficultyLevel: string | null;
  lifeguarded: string | null;
  stormActive: boolean;
  locationMetadata: LocationMetadata | null;
  /**
   * True when CONDITIONS_SAFETY_POLICY_V2_SPORTS lists this briefing's sport. The flag is
   * server-only, so the server passes the answer as data. Omitted or false: the legacy briefing,
   * byte-identical. True: the offshore caution follows the shared WFD-7 classifier
   * (offshore-sector.ts), or SUP-1 for the SUP sports, and missing storm evidence says so
   * (design 6.2, WP-S0 and WP-S3).
   */
  safetyPolicyV2?: boolean;
  /**
   * False when the storm evidence behind `stormActive` (weather code, or CAPE and lifted index)
   * is missing for the day. Read only under `safetyPolicyV2`, where it adds "Storm forecast
   * unavailable" unless a storm is already flagged. Defaults to true.
   */
  stormForecastKnown?: boolean;
  /**
   * False when the wind speed behind `windSpeedKmh` is missing (the page fills a gap with 0).
   * Read only under `safetyPolicyV2`, where the offshore check then reports "Offshore drift not
   * assessed" instead of reading 0 km/h as calm. Defaults to true.
   */
  windSpeedKnown?: boolean;
  /**
   * The gust in km/h at the reading behind `windSpeedKmh`, null or omitted when the provider had
   * none. Read only under `safetyPolicyV2`, by the SUP offshore-drift check (SUP-1), which has
   * gust thresholds: without it a light offshore wind reads "not assessed", because an unknown
   * gust could reach amber.
   */
  windGustKmh?: number | null;
};

export type Severity = "info" | "caution" | "danger";
export type BriefingHazard = { key: string; severity: Severity; title: string; detail: string };
export type SpotNote = { key: string; label: string; value: string };
export type CoastalBriefing = { headline: string; hazards: BriefingHazard[]; spotNotes: SpotNote[] };

// Initial thresholds (tunable). Named so they are easy to find and adjust.
const RIP_SWELL_M = 1.2;
const RIP_DANGER_SWELL_M = 2.0;
const COLD_SHOCK_C = 14;
const COLD_SHOCK_DANGER_C = 10;
const WATER_QUALITY_RAIN_MM = 10;
const WATER_QUALITY_DANGER_RAIN_MM = 20;
const BLOWN_OUT_WIND_KMH = 25;
const SIZE_GATE_SWELL_M = 1.8;
const SIZE_GATE_CAUTION_SWELL_M = 2.5;

const SEVERITY_ORDER: Record<Severity, number> = { danger: 0, caution: 1, info: 2 };

const RIP_SPORTS = new Set<CoastalSport>([
  "surf",
  "bodyboarding",
  "swim",
  "sup",
  "sup-touring",
  "sup-surf",
]);
const BLOWN_OUT_SPORTS = new Set<CoastalSport>([
  "sup",
  "sup-touring",
  "sup-surf",
  "foil",
  "downwind-foil",
  "wing-foil",
  "wind",
  "swim",
]);
const SIZE_GATE_SPORTS = new Set<CoastalSport>(["surf", "bodyboarding", "foil"]);

// Smallest circular angular difference, 0..180 degrees.
function angleDiff(a: number, b: number): number {
  return Math.abs((((a - b) % 360) + 540) % 360 - 180);
}

function isRockySeabed(seabed: string | null, bottomType: string | null): boolean {
  const s = (seabed ?? bottomType ?? "").toLowerCase();
  return s.includes("rock") || s.includes("reef") || s.includes("coral") || s.includes("mixed");
}

/** " (low ~HH:MM)"-style suffix, or "" when the time is only a lunar estimate. */
function lowTideTimeNote(input: CoastalBriefingInput, prefix: string): string {
  if (input.lowTideTimeKnown === false) return "";
  return ` (${prefix}~${formatTideTime(input.lowTideHours[0])})`;
}

export function deriveCoastalBriefing(input: CoastalBriefingInput): CoastalBriefing {
  const hazards: BriefingHazard[] = [];
  const offshore = input.beachFacingDeg != null ? (input.beachFacingDeg + 180) % 360 : null;
  const windFromOffshore =
    offshore != null && input.windDirDeg != null ? angleDiff(input.windDirDeg, offshore) : null;

  if (input.stormActive) {
    hazards.push({ key: "lightning", severity: "danger", title: "Thunderstorms forecast", detail: "Lightning risk. Get out of the water." });
  }

  if (
    RIP_SPORTS.has(input.sport) &&
    input.swellHeightM >= RIP_SWELL_M &&
    input.lowTideHours.length > 0 &&
    (windFromOffshore == null || windFromOffshore > 60)
  ) {
    hazards.push({
      key: "rip",
      severity: input.swellHeightM >= RIP_DANGER_SWELL_M ? "danger" : "caution",
      title: "Rip risk elevated",
      detail: `Strongest around the outgoing tide${lowTideTimeNote(input, "low ")}. Swim near the flags and do not fight the current.`,
    });
  }

  if (isRockySeabed(input.seabed, input.bottomType) && input.lowTideHours.length > 0) {
    hazards.push({
      key: "rocks-low-tide",
      severity: "caution",
      title: "Rocks exposed at low tide",
      detail: `Reef or rocks shallow around low tide${lowTideTimeNote(input, "")}. Launch and exit away from the rocks.`,
    });
  }

  if (input.nearbyCsoCount > 0 && input.trailingRain24h != null && input.trailingRain24h >= WATER_QUALITY_RAIN_MM) {
    const danger = input.sport === "swim" && input.trailingRain24h >= WATER_QUALITY_DANGER_RAIN_MM;
    hazards.push({
      key: "water-quality",
      severity: danger ? "danger" : "caution",
      title: "Water quality may be poor",
      detail: `Recent heavy rain (${Math.round(input.trailingRain24h)}mm) near a sewage overflow. Quality can stay poor for 24 to 48 hours.`,
    });
  }

  // Jellyfish is only flagged in the warm season (Jun-Oct) at high/peak level.
  // assessJellyfishRisk returns "high" for any 17-20C water regardless of
  // month, so without the season gate it would flag year-round and leave no
  // comfortable water-temp band (cold-shock fires below 14C).
  const jellyTemp = input.sstC ?? input.waterTempC;
  if (jellyTemp != null && input.month >= 6 && input.month <= 10) {
    const jelly = assessJellyfishRisk(jellyTemp, input.month);
    if (jelly.level === "high" || jelly.level === "peak") {
      hazards.push({ key: "jellyfish", severity: jelly.level === "peak" ? "danger" : "caution", title: jelly.label, detail: jelly.disclaimer });
    }
  }

  if (input.waterTempC != null && input.waterTempC < COLD_SHOCK_C) {
    hazards.push({
      key: "cold-shock",
      severity: input.waterTempC < COLD_SHOCK_DANGER_C ? "danger" : "caution",
      title: "Cold-shock risk",
      detail: `Cold water (${Math.round(input.waterTempC)} C). Wear 4/3mm or thicker, enter slowly, and do not go alone.`,
    });
  }

  if (input.safetyPolicyV2 === true) {
    hazards.push(...v2SafetyHazards(input));
  } else if (BLOWN_OUT_SPORTS.has(input.sport) && windFromOffshore != null && windFromOffshore < 45 && input.windSpeedKmh > BLOWN_OUT_WIND_KMH) {
    hazards.push({ key: "blown-out", severity: "caution", title: "Strong offshore wind", detail: "Risk of being blown out to sea. Stay close to shore and always use a leash." });
  }

  if (SIZE_GATE_SPORTS.has(input.sport) && input.swellHeightM >= SIZE_GATE_SWELL_M) {
    const heavy = isRockySeabed(input.seabed, input.bottomType) || (input.difficultyLevel ?? "").toLowerCase().includes("advanced");
    if (heavy) {
      hazards.push({
        key: "size-gate",
        severity: input.swellHeightM >= SIZE_GATE_CAUTION_SWELL_M ? "caution" : "info",
        title: "Powerful, advanced conditions",
        detail: "Solid surf on a reef or advanced break. Experienced riders only today.",
      });
    }
  }

  // Editorial extra hazards (info) before the sort, so ordering is consistent.
  const lm = input.locationMetadata;
  if (lm?.hazards?.length) {
    for (const h of lm.hazards) hazards.push({ key: `editorial:${h}`, severity: "info", title: "Local hazard", detail: h });
  }

  hazards.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  const spotNotes: SpotNote[] = [];
  if (input.lifeguarded) spotNotes.push({ key: "lifeguard", label: "Lifeguard", value: input.lifeguarded });
  if (lm?.access_notes) spotNotes.push({ key: "access", label: "Access", value: lm.access_notes });
  if (lm?.parking) spotNotes.push({ key: "parking", label: "Parking", value: lm.parking });
  if (lm?.facilities?.length) spotNotes.push({ key: "facilities", label: "Facilities", value: lm.facilities.join(", ") });

  return { headline: buildHeadline(input, hazards), hazards, spotNotes };
}

// ---------------------------------------------------------------------------
// Safety policy v2 (design 6.2, WP-S0), only when `safetyPolicyV2` is true.
//
// Offshore drift uses the one shared classifier the forecast safety rows use (WFD-7): red within
// 22.5 degrees of dead offshore, amber from 22.5 to 67.5 degrees, gated on usable wind (kite and
// windsurf 14 km/h, every other offshore-drift sport 12 km/h). It replaces the old page-only
// rule (within 45 degrees and above 25 km/h). The SUP sports use their own rule instead, as their
// v2 safety rows do (SUP-1, WP-S3): within 60 degrees of dead offshore, on sustained and gust
// thresholds (sup and sup-touring amber from 12 or 20 km/h and red from 20 or 30; sup-surf amber
// from 15 or 25 and red from 30 or 40). A check the inputs cannot make, WFD-7 or SUP-1, is shown as a
// caution row (owner decision O-20: conservative wording, never read as clear or as a note), and
// when no hazard is flagged it turns the clean headline into "Checks incomplete".
// ---------------------------------------------------------------------------

const OFFSHORE_UNAVAILABLE_KEY = "offshore-unavailable";
const UNAVAILABLE_CHECK_KEYS = new Set([OFFSHORE_UNAVAILABLE_KEY, STORM_FORECAST_UNAVAILABLE_KEY]);

/** SUP-1 thresholds per SUP briefing sport; any other offshore-drift sport uses WFD-7. */
const SUP_OFFSHORE_DRIFT_THRESHOLDS: Readonly<Partial<Record<CoastalSport, SupOffshoreDriftThresholds>>> = Object.freeze({
  sup: SUP_TOURING_OFFSHORE_DRIFT_THRESHOLDS,
  "sup-touring": SUP_TOURING_OFFSHORE_DRIFT_THRESHOLDS,
  "sup-surf": SUP_SURF_OFFSHORE_DRIFT_THRESHOLDS,
});

const OFFSHORE_UNAVAILABLE_ADVICE = "Check the wind angle on the beach before going out.";

function offshoreUnavailableHazard(missing: string): BriefingHazard {
  return {
    key: OFFSHORE_UNAVAILABLE_KEY,
    severity: "caution",
    title: "Offshore drift not assessed",
    detail: `${missing}, so offshore drift cannot be assessed. ${OFFSHORE_UNAVAILABLE_ADVICE}`,
  };
}

function offshoreBriefingHazard(result: OffshoreSectorResult): BriefingHazard | null {
  if (result.level === "red") {
    return {
      key: "blown-out",
      severity: "danger",
      title: "Offshore wind",
      detail: "Wind blowing straight out to sea. Risk of being blown offshore: stay close to shore and always use a leash.",
    };
  }
  if (result.level === "amber") {
    return {
      key: "blown-out",
      severity: "caution",
      title: "Cross-offshore wind",
      detail: "The wind has an offshore component. Stay close to shore, keep upwind of your exit and always use a leash.",
    };
  }
  if (result.level === "unavailable") {
    return offshoreUnavailableHazard(result.reason === "missing-beach-facing"
      ? "Beach orientation is missing"
      : result.reason === "missing-wind-direction"
        ? "The wind direction is missing"
        : "The wind speed is missing");
  }
  return null;
}

/** The SUP-1 row: "Offshore wind" at both levels, as the SUP v2 safety rows label amber. */
function supOffshoreBriefingHazard(result: SupOffshoreDriftAssessment): BriefingHazard | null {
  if (result.level === "red") {
    return {
      key: "blown-out",
      severity: "danger",
      title: "Offshore wind",
      detail: "Offshore wind can carry a paddler away from shore. Stay close to shore and always use a leash.",
    };
  }
  if (result.level === "amber") {
    return {
      key: "blown-out",
      severity: "caution",
      title: "Offshore wind",
      detail: "Offshore wind can make the return to shore difficult. Stay close to shore, keep upwind of your exit and always use a leash.",
    };
  }
  if (result.level === "unavailable") {
    return offshoreUnavailableHazard(result.provenance === "missing-local-geometry"
      ? "Beach orientation is missing"
      : "The wind direction, speed or gust is missing");
  }
  return null;
}

function v2SafetyHazards(input: CoastalBriefingInput): BriefingHazard[] {
  const hazards: BriefingHazard[] = [];
  const windSpeedKmh = input.windSpeedKnown === false ? null : input.windSpeedKmh;
  const supThresholds = SUP_OFFSHORE_DRIFT_THRESHOLDS[input.sport];
  if (supThresholds) {
    const offshore = supOffshoreBriefingHazard(assessSupOffshoreDrift({
      windSpeedKmh,
      windGustKmh: input.windGustKmh ?? null,
      windDirectionDeg: input.windDirDeg,
      beachFacingDeg: input.beachFacingDeg,
    }, supThresholds));
    if (offshore) hazards.push(offshore);
  } else if (BLOWN_OUT_SPORTS.has(input.sport)) {
    const offshore = offshoreBriefingHazard(classifyOffshoreSector({
      windSpeedKmh,
      windDirectionDeg: input.windDirDeg,
      beachFacingDeg: input.beachFacingDeg,
      ...(input.sport === "wind" ? { usableWindKmh: WIND_SPORTS_OFFSHORE_USABLE_WIND_KMH } : {}),
    }));
    if (offshore) hazards.push(offshore);
  }
  if (input.stormForecastKnown === false && !input.stormActive) {
    hazards.push({
      key: STORM_FORECAST_UNAVAILABLE_KEY,
      severity: "caution",
      title: STORM_FORECAST_UNAVAILABLE_LABEL,
      detail: STORM_FORECAST_UNAVAILABLE_DETAIL,
    });
  }
  return hazards;
}

function buildHeadline(input: CoastalBriefingInput, hazards: BriefingHazard[]): string {
  // An incomplete check is a caution row but not a hazard: it heads the line only when none is flagged.
  const serious = hazards.filter((h) => h.severity !== "info" && !UNAVAILABLE_CHECK_KEYS.has(h.key));
  if (serious.length > 0) {
    return `Heads up: ${serious.slice(0, 2).map((h) => h.title.toLowerCase()).join(", and ")}.`;
  }
  if (input.safetyPolicyV2 === true) {
    const incomplete = hazards.filter((h) => UNAVAILABLE_CHECK_KEYS.has(h.key));
    if (incomplete.length > 0) {
      return `Checks incomplete: ${incomplete.map((h) => h.title.toLowerCase()).join(", and ")}.`;
    }
  }
  const clauses: string[] = [];
  if (input.lifeguarded) clauses.push(input.lifeguarded.toLowerCase().includes("not") ? "not lifeguarded" : "lifeguarded");
  if (input.seabed === "sand" || (input.bottomType ?? "").toLowerCase().includes("sand")) clauses.push("sandy bottom");
  if (input.waterTempC != null) clauses.push(`${Math.round(input.waterTempC)} C water`);
  return clauses.length > 0 ? `Looking clean - ${clauses.join(", ")}.` : "Looking clean - no specific hazards flagged today.";
}
